/**
 * Owns the one download-reason dialog for the recruiter app.
 *
 * 1. During impersonation, call download with `IMPERSONATED_SESSION_REASON` and show snackbars
 *    without opening the dialog.
 * 2. Otherwise, store one active caller request and show its data description.
 * 3. Pass the parsed reason to the caller's download action.
 * 4. Close on success, or show a localized error and keep the dialog open on failure.
 * Caller access is in `useDownloadReasonDialog.ts`.
 */

import React, { type FC, useCallback, useRef, useState } from 'react';

import { DownloadReasonDialog as SharedDownloadReasonDialog, parseDownloadReason } from '@modernloop/shared/components';
import { useTranslation } from '@modernloop/i18n';
import { useSnackbar } from 'notistack';

import { useImpersonatingUserInfo } from 'src/hooks/useEmployeeId';

import DownloadReasonDialogContext from './DownloadReasonDialogContext';
import type { DownloadRequest } from './types';

type ActiveRequest = DownloadRequest & {
  parentId: string;
  requestId: number;
  finish: (didDownload: boolean) => void;
  isDownloading: boolean;
};

/** Supplies the required non-blank reason for an append that the backend will not store. */
const IMPERSONATED_SESSION_REASON = 'Impersonated session';

/** Keeps the request owner and completion callback with the active dialog state. */
const DownloadReasonDialogProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isImpersonating = useImpersonatingUserInfo() !== undefined;
  const [activeRequest, setActiveRequest] = useState<ActiveRequest | null>(null);
  const activeRequestRef = useRef<ActiveRequest | null>(null);
  const nextRequestId = useRef(0);

  const updateActiveRequest = useCallback((next: ActiveRequest | null) => {
    activeRequestRef.current = next;
    setActiveRequest(next);
  }, []);

  const showDownloadError = useCallback(
    (result: Awaited<ReturnType<DownloadRequest['download']>>) => {
      if (result.isOk()) {
        return;
      }

      if (result.error.type === 'NO_ROWS') {
        enqueueSnackbar(t('common:common.downloadReasonDialog.noDataToDownload'), {
          variant: 'info',
        });
        return;
      }

      enqueueSnackbar(t('common:common.downloadReasonDialog.downloadFailed'), {
        variant: 'error',
      });
    },
    [enqueueSnackbar, t]
  );

  const runImpersonatedDownload = useCallback(
    async (request: DownloadRequest, reason: Parameters<DownloadRequest['download']>[0]): Promise<boolean> => {
      const result = await request.download(reason);
      if (result.isOk()) {
        return true;
      }

      showDownloadError(result);
      return false;
    },
    [showDownloadError]
  );

  const requestBase = useCallback(
    (parentId: string, request: DownloadRequest): Promise<boolean> => {
      if (isImpersonating) {
        // Impersonated access is staff access. The backend requires a non-blank reason, then
        // acknowledges the append without creating a customer privacy ledger row.
        const parsedReason = parseDownloadReason(IMPERSONATED_SESSION_REASON).match(
          (reason) => reason,
          () => {
            // This fixed literal is valid by construction; keep the parser failure path for its contract.
            return undefined;
          }
        );
        if (parsedReason === undefined) {
          enqueueSnackbar(t('common:common.downloadReasonDialog.downloadFailed'), {
            variant: 'error',
          });
          return Promise.resolve(false);
        }

        activeRequestRef.current?.finish(false);
        updateActiveRequest(null);
        return runImpersonatedDownload(request, parsedReason);
      }

      return new Promise((resolve) => {
        const previous = activeRequestRef.current;
        previous?.finish(false);
        updateActiveRequest({
          ...request,
          parentId,
          requestId: nextRequestId.current++,
          finish: resolve,
          isDownloading: false,
        });
      });
    },
    [enqueueSnackbar, isImpersonating, runImpersonatedDownload, t, updateActiveRequest]
  );

  const closeOnParentUnmount = useCallback(
    (parentId: string) => {
      const current = activeRequestRef.current;
      if (current?.parentId === parentId) {
        current.finish(false);
        updateActiveRequest(null);
      }
    },
    [updateActiveRequest]
  );

  const closeDialog = useCallback(() => {
    const current = activeRequestRef.current;
    if (current && !current.isDownloading) {
      current.finish(false);
      updateActiveRequest(null);
    }
  }, [updateActiveRequest]);

  const handleDownload = useCallback(
    async (reason: Parameters<DownloadRequest['download']>[0]) => {
      const current = activeRequestRef.current;
      if (!current || current.isDownloading) {
        return;
      }

      const requestId = current.requestId;
      updateActiveRequest({ ...current, isDownloading: true });
      const result = await current.download(reason);
      if (activeRequestRef.current?.requestId !== requestId) {
        return;
      }

      if (result.isOk()) {
        current.finish(true);
        updateActiveRequest(null);
        return;
      }

      showDownloadError(result);
      if (result.error.type === 'NO_ROWS') {
        current.finish(false);
        updateActiveRequest(null);
        return;
      }

      updateActiveRequest({ ...current, isDownloading: false });
    },
    [showDownloadError, updateActiveRequest]
  );

  return (
    <DownloadReasonDialogContext.Provider value={{ requestBase, closeOnParentUnmount }}>
      {children}
      <SharedDownloadReasonDialog
        open={activeRequest !== null}
        dataDescription={activeRequest?.dataDescription ?? ''}
        isDownloading={activeRequest?.isDownloading ?? false}
        onDownload={(reason) => void handleDownload(reason)}
        onClose={closeDialog}
      />
    </DownloadReasonDialogContext.Provider>
  );
};

export default DownloadReasonDialogProvider;
